<script setup>
  import { computed, provide, ref } from "vue";
  import { storeToRefs } from "pinia";
  import CollapseExpandAllButton from "@/components/episto-ui/collapse-card/CollapseExpandAllButton.vue";
  import { Button } from "@/components/ui/button";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import { IndexPageFilters, IndexPageLayout } from "@/components/App/IndexPage";
  import { useMCampaignTargetsStore } from "@stores/mCampaignTargets";
  import { useMCampaignQuotasStore } from "@stores/mCampaignQuotas";
  import { useUnsavedChangesGuard } from "@/utils/composables/useUnsavedChangesGuard";
  import { useMCampaignUnsavedChanges } from "@/utils/composables/useMCampaignUnsavedChanges";
  import { useTargetsAndQuotasFilters } from "@utils/composables/filters/useTargetsAndQuotasFilters";
  import { Table, TableHead, TableHeader, TableRow } from "@/components/ui/table";
  import TargetRows from "./TargetRows.vue";
  import { useSelection } from "./composables/useSelection";

  const mCampaignTargetsStore = useMCampaignTargetsStore();
  const { campaignTargets, searchQuery } = storeToRefs(mCampaignTargetsStore);

  const mCampaignQuotasStore = useMCampaignQuotasStore();
  const { campaignQuotas, allModalities } = storeToRefs(mCampaignQuotasStore);

  const selection = useSelection();

  provide("selection", selection);
  provide("allTargets", campaignTargets);
  provide("allGroups", campaignQuotas);
  provide("allQuotas", allModalities);

  const { filterConfigs, activeFiltersCount, handleFilterChange, handleClear } = useTargetsAndQuotasFilters();

  const expandedTokens = ref([]);

  const allExpanded = computed(
    () =>
      campaignTargets.value.length > 0 &&
      campaignTargets.value.every((t) => expandedTokens.value.includes(t.token)),
  );

  const handleToggle = (token) => {
    const set = new Set(expandedTokens.value);
    if (set.has(token)) set.delete(token);
    else set.add(token);
    expandedTokens.value = Array.from(set);
  };

  const expandAll = () => {
    const tokens = new Set(expandedTokens.value);
    campaignTargets.value.forEach((t) => tokens.add(t.token));
    expandedTokens.value = Array.from(tokens);
  };

  const collapseAll = () => {
    expandedTokens.value = expandedTokens.value.filter(
      (tok) => !campaignTargets.value.some((t) => t.token === tok),
    );
  };

  useUnsavedChangesGuard(useMCampaignUnsavedChanges().hasUnsavedChanges);
</script>

<template>
  <IndexPageLayout data-cy="master-campaign-targets-and-quotas-page">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search target, question, or modality...">
        <template #filters>
          <FilterDropdown
            :filters="filterConfigs"
            :active-filters-count="activeFiltersCount"
            @toggle="handleFilterChange"
            @clear="handleClear" />
          <CollapseExpandAllButton
            :expanded="allExpanded"
            size="sm"
            @toggle="allExpanded ? collapseAll() : expandAll()" />
        </template>
        <template #actions>
          <span
            v-if="selection.selectedCount.value > 0"
            class="text-sm text-muted-foreground">
            {{ selection.selectedCount.value }} selected
          </span>
          <Button
            v-if="selection.selectedCount.value > 0"
            variant="ghost"
            size="sm"
            class="text-sm"
            @click="selection.clearAll">
            Clear
          </Button>
        </template>
      </IndexPageFilters>
    </template>

    <EmptyState
      v-if="campaignTargets.length === 0"
      title="No targets match the current filters." />

    <div
      v-else
      class="overflow-hidden rounded-lg border bg-background [&_td]:px-3 [&_td]:py-2 [&_td]:text-13 [&_th]:h-auto [&_th]:bg-muted [&_th]:px-3 [&_th]:py-2.5 [&_th]:text-3xs [&_th]:font-medium [&_th]:uppercase [&_th]:tracking-[0.1em] [&_th]:text-foreground-subtle">
      <Table class="table-fixed">
        <TableHeader>
          <TableRow class="hover:bg-transparent">
            <TableHead class="w-12" />
            <TableHead class="w-16" />
            <TableHead>Target / Quota</TableHead>
            <TableHead class="w-28 text-center">Target</TableHead>
            <TableHead class="w-40 text-center">Tolerance min (%)</TableHead>
            <TableHead class="w-44 text-center">Bidder target (n)</TableHead>
          </TableRow>
        </TableHeader>
        <TargetRows
          v-for="target in campaignTargets"
          :key="target.token"
          :target="target"
          :expanded="expandedTokens.includes(target.token)"
          @toggle="handleToggle" />
      </Table>
    </div>
  </IndexPageLayout>
</template>
